import { createTheme } from '@mui/material/styles';
import {
  FONT_STACK,
  light,
  dark,
  radius,
  buildSoftShadows,
} from './tokens';

const softShadows = buildSoftShadows();

/**
 * MUI theme for Aromahpure — light-first, calm surfaces, sage accent.
 * Glass reserved for chrome (AppBar / bottom nav); content stays opaque.
 */
export default function createAppTheme(mode = 'light') {
  const isDark = mode === 'dark';
  const t = isDark ? dark : light;

  return createTheme({
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    },
    palette: {
      mode,
      primary: {
        main: t.accent,
        light: '#8A9B92',
        dark: '#45554E',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#C5D4CC',
        light: '#E4EDE8',
        dark: '#9AADA3',
      },
      divider: t.hairline,
      background: {
        default: t.bg,
        paper: t.surface,
      },
      text: {
        primary: t.text,
        secondary: t.textSecondary,
      },
      success: { main: '#34C759', light: '#5DD879', dark: '#248A3D' },
      warning: { main: '#FF9F0A', light: '#FFB340', dark: '#C77700' },
      error: { main: '#FF3B30', light: '#FF6961', dark: '#D70015' },
      info: {
        main: t.accent,
        light: '#8A9B92',
        dark: '#45554E',
      },
      grey: {
        50: '#F9FAF9',
        100: '#F5F5F7',
        200: '#E5E5EA',
        300: '#D1D1D6',
        400: '#A8A8AE',
        500: '#8E8E93',
        600: '#636366',
        700: '#48484A',
        800: '#3A3A3C',
        900: '#1C1C1E',
      },
    },
    typography: {
      fontFamily: FONT_STACK,
      h1: { fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.15 },
      h2: { fontWeight: 700, letterSpacing: '-0.022em', lineHeight: 1.2 },
      h3: { fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25 },
      h4: { fontWeight: 600, letterSpacing: '-0.018em', lineHeight: 1.3 },
      h5: { fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.35 },
      h6: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 },
      subtitle1: { fontWeight: 500, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 500, letterSpacing: '-0.008em' },
      button: {
        fontWeight: 590,
        textTransform: 'none',
        letterSpacing: '-0.01em',
      },
      body1: {
        fontSize: '0.9375rem',
        fontWeight: 400,
        letterSpacing: '-0.01em',
        lineHeight: 1.47,
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        letterSpacing: '-0.008em',
        lineHeight: 1.43,
      },
      caption: {
        fontSize: '0.75rem',
        letterSpacing: '-0.006em',
        lineHeight: 1.35,
      },
    },
    shape: {
      borderRadius: radius.md,
    },
    shadows: softShadows,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: FONT_STACK,
            backgroundColor: t.bg,
            backgroundImage: 'none',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: radius.md,
            border: `1px solid ${t.hairline}`,
            backgroundColor: t.surface,
            backgroundImage: 'none',
            transition: 'border-color 0.18s ease',
            '&:hover': {
              transform: 'none',
              boxShadow: 'none',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 10,
            minHeight: 44,
            padding: '10px 18px',
            textTransform: 'none',
            fontWeight: 590,
            boxShadow: 'none',
            transition: 'background-color 0.18s ease, opacity 0.18s ease, border-color 0.18s ease',
            '&:active': { opacity: 0.88 },
          },
          contained: {
            backgroundColor: t.accent,
            backgroundImage: 'none',
            '&:hover': {
              backgroundColor: isDark ? '#8A9B92' : '#45554E',
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            backgroundColor: t.accent,
            '&:hover': {
              backgroundColor: isDark ? '#8A9B92' : '#45554E',
            },
          },
          outlined: {
            borderColor: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(60, 60, 67, 0.22)',
            '&:hover': {
              borderColor: t.accent,
              backgroundColor: t.accentMuted,
            },
          },
          sizeLarge: {
            minHeight: 48,
            padding: '12px 22px',
            fontSize: '1.0625rem',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: 'background-color 0.18s ease',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundColor: isDark
              ? 'rgba(28, 28, 30, 0.78)'
              : 'rgba(255, 255, 255, 0.78)',
            backdropFilter: 'saturate(180%) blur(20px)',
            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            borderBottom: `1px solid ${t.hairline}`,
            color: 'inherit',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: t.hairline,
          },
          elevation1: {
            boxShadow: 'none',
            border: `1px solid ${t.hairline}`,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark
              ? 'rgba(118, 118, 128, 0.18)'
              : 'rgba(118, 118, 128, 0.08)',
            transition: 'background-color 0.18s ease',
            '&:hover': {
              backgroundColor: isDark
                ? 'rgba(118, 118, 128, 0.24)'
                : 'rgba(118, 118, 128, 0.12)',
            },
            '&.Mui-focused': {
              backgroundColor: t.surface,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(60, 60, 67, 0.18)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.accent,
              borderWidth: 1.5,
            },
          },
          input: {
            paddingTop: 14,
            paddingBottom: 14,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.lg,
            boxShadow: isDark
              ? '0 24px 80px rgba(0,0,0,0.55)'
              : '0 24px 80px rgba(15, 23, 20, 0.14)',
            border: `1px solid ${t.hairline}`,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: radius.md },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            fontWeight: 500,
            letterSpacing: '-0.01em',
          },
          filled: {
            backgroundColor: t.accentMuted,
            color: t.text,
          },
          filledPrimary: {
            backgroundColor: t.accent,
            color: '#ffffff',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: {
            height: 2.5,
            borderRadius: 2,
            backgroundColor: t.accent,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: 44,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            fontSize: '0.9375rem',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            border: `1px solid ${t.hairline}`,
            backgroundColor: t.surface,
            boxShadow: 'none',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            '& .MuiTableHead-root .MuiTableCell-root': {
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.04)'
                : 'rgba(60, 60, 67, 0.04)',
              fontWeight: 600,
              fontSize: '0.6875rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: t.textSecondary,
              borderBottom: `1px solid ${t.hairline}`,
              py: 1.5,
            },
            '& .MuiTableBody-root .MuiTableCell-root': {
              borderBottom: isDark
                ? '1px solid rgba(255, 255, 255, 0.06)'
                : '1px solid rgba(60, 60, 67, 0.08)',
              py: 1.75,
            },
            '& .MuiTableRow-root': {
              transition: 'background-color 0.15s ease',
              '&:hover': {
                backgroundColor: t.accentMuted,
              },
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 650,
            letterSpacing: '-0.02em',
            fontSize: '1.125rem',
            padding: '20px 24px 12px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '8px 24px 20px' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 20px 20px',
            gap: 8,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            height: 4,
            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(60, 60, 67, 0.1)',
          },
          bar: {
            borderRadius: 999,
            backgroundColor: t.accent,
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? 'rgba(118, 118, 128, 0.18)'
              : 'rgba(118, 118, 128, 0.12)',
            borderRadius: 10,
            padding: 2,
          },
          grouped: {
            border: 0,
            margin: 0,
            borderRadius: '8px !important',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            padding: '6px 14px',
            color: t.textSecondary,
            '&.Mui-selected': {
              backgroundColor: t.surface,
              color: t.text,
              boxShadow: '0 1px 2px rgba(15,23,20,0.08)',
              '&:hover': { backgroundColor: t.surface },
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: `${radius.md}px !important`,
            border: `1px solid ${t.hairline}`,
            boxShadow: 'none',
            backgroundImage: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            height: 64,
            backgroundColor: isDark
              ? 'rgba(28, 28, 30, 0.86)'
              : 'rgba(255, 255, 255, 0.86)',
            backdropFilter: 'saturate(180%) blur(20px)',
            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            borderTop: `1px solid ${t.hairline}`,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 64,
            color: isDark ? 'rgba(235, 235, 245, 0.55)' : 'rgba(60, 60, 67, 0.55)',
            '&.Mui-selected': { color: t.accent },
          },
          label: {
            fontSize: '0.6875rem',
            letterSpacing: '-0.01em',
            '&.Mui-selected': { fontSize: '0.6875rem' },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius.md,
            border: `1px solid ${t.hairline}`,
            boxShadow: isDark
              ? '0 16px 48px rgba(0,0,0,0.45)'
              : '0 16px 48px rgba(15, 23, 20, 0.12)',
            backgroundImage: 'none',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: radius.sm,
            fontSize: '0.75rem',
            fontWeight: 500,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            minHeight: 44,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(60, 60, 67, 0.08)',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: t.hairline },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: 16,
            paddingRight: 16,
            '@media (min-width: 600px)': {
              paddingLeft: 24,
              paddingRight: 24,
            },
          },
        },
      },
      MuiFocusVisible: {
        styleOverrides: {
          root: {
            outline: `2px solid ${t.accent}`,
            outlineOffset: 2,
          },
        },
      },
    },
  });
}
